/**
 * Inspection Series-specific preferences hook.
 *
 * Wraps useListingPreferences with:
 *  - The series legacy migrator (converts old { hiddenColumns, stateFilters, ... } shapes).
 *  - Column policy resolved from the current scope.
 *  - The scope-appropriate localStorage cache key.
 *  - Default series filter values.
 *
 * Returns the same shape as useListingPreferences so the parent component
 * can use it directly to initialize useListingQueryState.
 *
 * Series-specific adaptations (task-01 inventory #4):
 *  - Series filters are always-active — there is no active/inactive toggle UI.
 *  - `activeFilterKeys` from preferences is unused by the filter config builder;
 *    all filters are rendered based on prop flags (hasShowXxxFilter), not this field.
 *  - Page sizes are [25, 50, 100, 250] — four options.
 */
import { useMemo } from 'react';
import { useListingPreferences } from '@hooks/listings';
import { INITIAL_SERIES_FILTERS, SERIES_DEFAULT_PAGE_SIZE } from '../types';
import { columnPolicyForScope, cacheKeyForScope } from '../adapters/seriesColumnPolicy';
import { seriesLegacyMigrator } from '../adapters/seriesLegacyMigrator';
/**
 * Loads, migrates, and saves Inspection Series listing preferences.
 *
 * The returned `preferences` object (when non-null) provides:
 *  - pageSize, sort, filters, activeFilterKeys, visibleColumnKeys
 *
 * When `preferences` is null, the parent should use feature defaults
 * (INITIAL_SERIES_FILTERS, default sort, SERIES_PAGE_SIZES[0]).
 */
export function useSeriesPreferences(options) {
    const { scope } = options;
    const cacheKey = useMemo(() => cacheKeyForScope(scope), [scope]);
    const policy = useMemo(() => columnPolicyForScope(scope), [scope]);
    return useListingPreferences({
        cacheKey,
        policy,
        defaultFilters: INITIAL_SERIES_FILTERS,
        defaultPageSize: SERIES_DEFAULT_PAGE_SIZE,
        legacyMigrator: seriesLegacyMigrator
    });
}
